import { gql } from '@apollo/client';
import { Button } from '@mnd-frontend/ui/publish/components';
import { SPACING_1, SPACING_3 } from '@mnd-frontend/ui/shared/tokens';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import { useNewsroomSetup } from '../../../contexts/NewsroomSetupContext';
import { useSeenOnboardingWizardFromFooterMutation } from '../../../generated/hooks';
import type { NewsroomSetupSteps } from '../../../modules/newsroomSetup/types';

gql`
  mutation seenOnboardingWizardFromFooter($pressroomId: ID!) {
    noticeConfirm(input: { notice: onboardingWizard, pressroomId: $pressroomId }) {
      pendingNotices {
        id
        onboardingWizard
      }
      pressroom {
        id
        dashboard {
          id
          ... on AccountVerificationPending {
            id
            user {
              id
              locale2
            }
          }
        }
      }
    }
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${SPACING_1};
  margin-top: auto;
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  gap: ${SPACING_1} ${SPACING_3};
  justify-content: flex-end;
`;

type Props = {
  nextStep?: NewsroomSetupSteps;
  previousStep?: NewsroomSetupSteps;
  showReadyToGoLive?: boolean;
  isSaving?: boolean;
  canContinue?: boolean;
};

const Footer: FC<Props> = ({
  nextStep,
  previousStep,
  showReadyToGoLive,
  isSaving,
  canContinue,
}) => {
  const [t] = useTranslation();
  const { pressroomId } = useCurrentPressroom();
  const { setNewsroomSetupStep } = useNewsroomSetup();

  const [seenOnboardingWizard, { loading }] = useSeenOnboardingWizardFromFooterMutation({
    onCompleted: () => {
      setNewsroomSetupStep(undefined);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    variables: {
      pressroomId,
    },
  });

  return (
    <Container>
      {previousStep ? (
        <Button
          configuration="text"
          type="button"
          onClick={() => {
            setNewsroomSetupStep(previousStep);
          }}
        >
          {t('common_previous')}
        </Button>
      ) : (
        <div />
      )}
      <Buttons>
        <Button
          configuration="text"
          loading={loading}
          type="button"
          onClick={async () => {
            if (showReadyToGoLive === false) {
              await seenOnboardingWizard();
            } else {
              setNewsroomSetupStep(nextStep);
            }
          }}
        >
          {t('onboarding-skip-step')}
        </Button>
        <Button type="submit" disabled={!canContinue} loading={isSaving}>
          {t('common_next')}
        </Button>
      </Buttons>
    </Container>
  );
};

export default Footer;
